import { useCallback, useEffect, useMemo, useState } from "react";
import "./TidyPage.css";
import { AppShell, SideAction, SideGroup } from "../app/AppShell";
import type { IconName } from "../components/Icon";
import { Button } from "../components/Button";
import { Alert, EmptyState, Loading } from "../components/Card";
import { PageHead } from "../components/Page";
import { Select } from "../components/Field";
import { Icon } from "../components/Icon";
import { projectApi } from "../lib/project";
import type { Project } from "../lib/project";
import { Chip } from "../components/Status";
import { TidyLegend, TidyResult, TidyScope, TidyTable } from "../components/Tidy";
import type { IssueLite, TidyRow } from "../components/Tidy";
import { errText, issueActsApi, tasksApi } from "../lib/tasks";
import type { Issue } from "../lib/tasks";
import { KIND, KIND_KEYS, PROPOSAL_STATUS, tidyApi } from "../lib/tidy";
import type { Proposal, ProposalStatus, Skip, TidyState } from "../lib/tidy";
import { tx } from "../i18n";

/* ══ 이슈 정리 ══
   채택 시안 **안 A · 표 한 벌**(Figma 52-3, 사람 확정 2026-10-01) 그대로다. 읽는 문은
   `GET /tidy/proposals`·`GET /tidy/state`·`GET /tasks`, 쓰는 문은 적용(사람만)·기각·정리 요청
   이다(`crates/api/src/tidy.rs`). 왜 이 모양인지는 `docs/decisions/tidy.md`.

   고르는 축이 둘이다 — 건별(줄 앞 칸)과 갈래별(사이드바). 갈래를 고르면 표에 그 갈래만 남아
   「전체 선택」이 곧 **그 갈래 전체**가 된다. 보이는 줄 판정은 `visible` **한 곳**이다 — 고르기·
   전체 선택·건수·선택 막대가 다 이것을 읽어야 「보이는 것」과 「고른 것」이 안 갈린다. */

type StatusFilter = ProposalStatus | "all";
/** 레일 줄 아이콘 — 표의 글자 표식(○ ● ⊘)과 같은 뜻을 아이콘으로. 레일에 둘을 겹쳐 그리지 않는다. */
const TIDY_STATUS_ICON: Record<StatusFilter, IconName> = {
  open: "circleDot",
  applied: "circleCheck",
  dismissed: "circleOff",
  all: "layers",
};

export function TidyPage({ now }: { now?: number }) {
  const [proposals, setProposals] = useState<Proposal[] | null>(null);
  const [issues, setIssues] = useState<Issue[] | null>(null);
  const [state, setState] = useState<TidyState | null>(null);
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [actionErr, setActionErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [requesting, setRequesting] = useState(false);
  const [statusF, setStatusF] = useState<StatusFilter>("open");
  const [kindF, setKindF] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [projects, setProjects] = useState<Project[]>([]);
  // 프로젝트는 **보기 거르개**다 — 정리가 훑는 범위(이 설치 전체)는 그대로다. 받은 목록을 접기만 한다.
  const [projectF, setProjectF] = useState("");
  const [copyNote, setCopyNote] = useState<string | null>(null);
  const [copying, setCopying] = useState(false);
  const [result, setResult] = useState<{ applied: number; skipped: { proposalId: string; taskId: string; skip: Skip }[] } | null>(null);

  const load = useCallback(async () => {
    try {
      const [ps, ts, s] = await Promise.all([tidyApi.list(), tasksApi.list(), tidyApi.state()]);
      setProposals(ps);
      setIssues(ts);
      setState(s);
      setLoadErr(null);
    } catch (e) {
      setLoadErr(errText(e));
    }
  }, []);
  useEffect(() => {
    load();
    projectApi.list().then(setProjects, () => undefined);
  }, [load]);

  const byId = useMemo(() => {
    const m = new Map<string, IssueLite>();
    for (const t of issues ?? []) m.set(t.id, { id: t.id, title: t.title, status: t.status, projectId: t.projectId ?? null });
    return m;
  }, [issues]);

  /** 열린 이슈 수 — 이미 받은 `GET /tasks` 를 접는다(요청이 안 는다). 앵커는 판정 대상이 아니라 뺀다. */
  const openCount = issues
    ? issues.filter((t) => t.status !== "done" && t.status !== "cancelled" && t.type !== "tidy").length
    : null;

  /** 프로젝트 거르개 — 이슈가 목록에 없으면(지워졌거나 못 읽음) 어느 프로젝트인지 모르니 「전체」에서만 보인다. */
  const inProject = (p: Proposal) => {
    if (!projectF) return true;
    const t = byId.get(p.taskId);
    if (!t) return false;
    return projectF === "-" ? !t.projectId : t.projectId === projectF;
  };
  const all = (proposals ?? []).filter(inProject);
  const inStatus = all.filter((p) => statusF === "all" || p.status === statusF);
  const visible: TidyRow[] = inStatus
    .filter((p) => kindF === null || p.kind === kindF)
    .map((p) => ({ p, task: byId.get(p.taskId), ref: p.refTaskId ? byId.get(p.refTaskId) : undefined }));

  /** 거르개를 바꾸면 선택을 비운다 — 가려진 줄이 선택에 남으면 사람이 중복 둘을 정리하려다
   *  「이미 끝남」까지 함께 움직인다. */
  function filter(next: { status?: StatusFilter; kind?: string | null; project?: string }) {
    if (next.project !== undefined) setProjectF(next.project);
    if (next.status !== undefined) setStatusF(next.status);
    if (next.kind !== undefined) setKindF(next.kind);
    setSelected(new Set());
  }

  function toggle(id: string) {
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  }
  function toggleAll() {
    const open = visible.filter((r) => r.p.status === "open").map((r) => r.p.id);
    setSelected((s) => (open.every((id) => s.has(id)) ? new Set() : new Set(open)));
  }

  /** 적용 — **건별로 부른다.** 결과 블록은 「적용 n · 건너뜀 m」을 늘 둘 다 말한다. 끝나면
   *  서버 상태를 다시 그린다(낙관적으로 줄을 지우지 않는다). */
  async function applySelected() {
    const picked = visible.filter((r) => selected.has(r.p.id) && r.p.status === "open");
    if (!picked.length) return;
    setBusy(true);
    setActionErr(null);
    let applied = 0;
    const skipped: { proposalId: string; taskId: string; skip: Skip }[] = [];
    try {
      for (const r of picked) {
        const out = await tidyApi.apply(r.p.id);
        if (out.applied) applied += 1;
        else skipped.push({ proposalId: r.p.id, taskId: r.p.taskId, skip: out.skipped });
      }
    } catch (e) {
      setActionErr(errText(e));
    } finally {
      setResult({ applied, skipped });
      setSelected(new Set());
      await load();
      setBusy(false);
    }
  }

  async function dismiss(id: string) {
    setBusy(true);
    setActionErr(null);
    try {
      await tidyApi.dismiss(id);
      setSelected((s) => {
        const n = new Set(s);
        n.delete(id);
        return n;
      });
    } catch (e) {
      setActionErr(errText(e));
    } finally {
      await load();
      setBusy(false);
    }
  }

  /** 정리 배차 프롬프트 — 앵커 이슈의 기획 트랙 프롬프트를 서버가 만든다(화면이 문장을 짓지 않는다). */
  async function copyPrompt() {
    const a = state?.anchor;
    if (!a) return;
    setCopying(true);
    setActionErr(null);
    try {
      const p = await issueActsApi.prompt(a.id, "plan");
      await navigator.clipboard.writeText(p.prompt);
      setCopyNote(tx("tidy.copied"));
    } catch (e) {
      setActionErr(errText(e));
    } finally {
      setCopying(false);
    }
  }

  async function request() {
    setRequesting(true);
    setActionErr(null);
    try {
      setState(await tidyApi.request());
    } catch (e) {
      setActionErr(errText(e));
    } finally {
      setRequesting(false);
    }
  }

  const count = (s: StatusFilter) => (s === "all" ? all.length : all.filter((p) => p.status === s).length);
  const kindCount = (k: string | null) => inStatus.filter((p) => k === null || p.kind === k).length;

  /* 사이드바는 **이 화면의 맥락 메뉴**라 이 화면이 그린다. 정리 요청은 여기 — 표 바닥의 적용과
     떨어져 있다(무게가 다르다). */
  const side = (
    <>
      <TidyScope state={state} openCount={openCount} busy={requesting} onRequest={request} now={now} />
      <SideGroup>{tx("field.status")}</SideGroup>
      {(["open", "applied", "dismissed", "all"] as StatusFilter[]).map((s) => (
        <SideAction key={s} icon={TIDY_STATUS_ICON[s]} active={statusF === s} onClick={() => filter({ status: s })} count={proposals ? count(s) : null}>
          {s === "all" ? tx("board.all") : PROPOSAL_STATUS[s].label}
        </SideAction>
      ))}
      <SideGroup>{tx("alarm.table.kind")}</SideGroup>
      <SideAction icon="layers" active={kindF === null} onClick={() => filter({ kind: null })} count={proposals ? kindCount(null) : null}>
        {tx("board.all")}
      </SideAction>
      {KIND_KEYS.map((k) => (
        <SideAction key={k} icon="tag" tip={KIND[k].label} active={kindF === k} onClick={() => filter({ kind: k })} count={proposals ? kindCount(k) : null}>
          {KIND[k].glyph} {KIND[k].label}
        </SideAction>
      ))}
    </>
  );

  const statusLabel = statusF === "all" ? tx("board.all") : `${PROPOSAL_STATUS[statusF].glyph} ${PROPOSAL_STATUS[statusF].label}`;

  return (
    <AppShell route="/tidy" sidebar={side} sidebarTitle={tx("nav.tidy")}>
      <div className="tidy">
        <PageHead
          title={tx("nav.tidy")}
          meta={
            <Chip tone="muted">
              {statusLabel} {proposals ? tx("alarm.page.count", { n: visible.length }) : "…"}
            </Chip>
          }
          lead={tx("tidy.lead")}
        />
        <div className="b-filterbar tidy__filters">
          <div className="b-filterbar-chips">
            <Select size="md" value={projectF} onChange={(e) => filter({ project: e.target.value })} aria-label={tx("tidy.project")}>
              <option value="">{tx("tidy.allProjects")}</option>
              {projects.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
              <option value="-">{tx("tidy.noProject")}</option>
            </Select>
          </div>
          <div className="b-filterbar-end">
            <Button
              size="md"
              variant="ghost"
              onClick={copyPrompt}
              loading={copying}
              disabled={!state?.anchor}
              title={state?.anchor ? undefined : tx("tidy.copyPromptNoAnchor")}
            >
              <Icon name="copy" /> {tx("tidy.copyPrompt")}
            </Button>
          </div>
        </div>
        {copyNote ? <Alert tone="success">{copyNote}</Alert> : null}
        {actionErr ? <Alert tone="error">{actionErr}</Alert> : null}
        {result ? <TidyResult applied={result.applied} skipped={result.skipped} /> : null}
        <TidyBody
          loading={proposals === null && !loadErr}
          loadErr={loadErr}
          onRetry={load}
          rows={visible}
          neverSwept={state !== null && state.lastSweepAt === null}
          statusF={statusF}
          onRequest={request}
          requesting={requesting}
        >
          <TidyTable
            rows={visible}
            selected={selected}
            busy={busy}
            onToggle={toggle}
            onToggleAll={toggleAll}
            onDismiss={dismiss}
            onApply={applySelected}
            onClear={() => setSelected(new Set())}
            now={now}
          />
          <TidyLegend />
        </TidyBody>
      </div>
    </AppShell>
  );
}

/** 상태 넷 — 로딩 · 오류 · 비었음(두 갈래) · 있음. 공통 프레임 55-2 ① 그대로다. **빈 상태는
 *  둘이다**: 「훑었는데 없음」과 「한 번도 안 훑음」은 사람이 할 일이 정반대다. */
export function TidyBody({
  loading,
  loadErr,
  onRetry,
  rows,
  neverSwept,
  statusF,
  onRequest,
  requesting,
  children,
}: {
  loading: boolean;
  loadErr: string | null;
  onRetry: () => void;
  rows: TidyRow[];
  neverSwept: boolean;
  statusF: StatusFilter;
  onRequest: () => void;
  requesting?: boolean;
  children: React.ReactNode;
}) {
  if (loadErr)
    return (
      <div className="tidy__state">
        <Alert tone="error">
          {tx("tidy.loadFailed", { err: loadErr })}
        </Alert>
        <Button size="md" onClick={onRetry}>
          {tx("tidy.reload")}
        </Button>
      </div>
    );
  if (loading)
    return (
      <Loading text={tx("tidy.loading")} />
    );
  if (!rows.length) {
    if (neverSwept)
      return (
        <EmptyState
          title={tx("tidy.empty.never")}
          desc={tx("tidy.empty.neverDesc")}
          action={
            <Button size="md" onClick={onRequest} loading={requesting}>
              ↻ {tx("tidy.request")}
            </Button>
          }
        />
      );
    return (
      <EmptyState
        icon="check"
        title={tx(statusF === "open" ? "tidy.empty.noOpen" : "tidy.empty.noMatch")}
        desc={tx("tidy.empty.desc")}
      />
    );
  }
  return <>{children}</>;
}
